import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Spark EUV Configurator (linkId 2372).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2372.ts`; shared UI boilerplate
 * (staticTranslations / saveFormConfig) follows the 2378 project.
 *
 * Conversion notes:
 *  - 1.0 `removeAccessories` + `defaultAccessory` become a shared graphics
 *    `groupId` + `isDefault` (2.0's single-select mechanism), namespaced per
 *    trim because the SDK resolves groupIds model-wide.
 *  - 1.0 `forColor` (allow-list of colour indices) is inverted into 2.0's
 *    `notAvailableForColor` deny-list of colour ids.
 *  - `interactionApplyMessage`/`applyInteractions` become
 *    `affectedInteractionOnApply`; `includedPackages` become
 *    `dependencyPackages`; `brandItemCode` becomes `autoIntelRPOCode`.
 *  - Dropped, with no 2.0 equivalent: `video` Welcome Animation interactions,
 *    feature `tempSkuToLoad`/`replaceAsset`, `interactionRemoveMessage`,
 *    interaction `meshAnimation`/`glowMesh`.
 *  - Broken references in the 1.0 source (dangling ids, duplicate accessory
 *    ids, identifiers naming assets the variant never loads) were repaired —
 *    see conversion-report-2372.txt.
 *
 * Assets live in public/assets/{images,videos}/2372/.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    SparkEUV: {
      variants: {
        'activ-fwd': {
          // Year sent to Salesforce in state_info.variant ("YYYY <name>"); API-only.
          leadModelYear: '2026',
          configurationId: 'u5qm0qtkgk60',
          name: {
            en: 'ACTIV',
            ar: 'ACTIV'
          },
          description: {
            en: 'FWD',
            ar: 'الدفع الأمامي'
          },
          drivetrain: 'FWD',
          trim: 'Activ',
          image: '',
          price: {
            KSA: 81420,
            UAE: 79900,
            OMR: 8468,
            BHD: 8251,
            QAR: 79032,
            JOD: 15415,
            KW: 6731,
            IQD: 21712,
            LBP: 21712,
            default: 79900,
          },
          basePrice: {
            UAE: 79900,
            KSA: 79800,
            KW: 6330,
            OMR: 8360,
            BHD: 8207,
            QAR: 79900,
            IQD: 28507745,
            LBP: 1948011087,
            default: 79900
          },
          index: 0,
          isDefault: true,
          ar: {
            enable: false,
            redirection: false
          },
          autoIntelRPOCode: '1SH',
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          brandItemCode: 'CHSP01',
        }
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2372/HDRI/hdri_1.webp',
          title: {
            en: 'City Landscape 1',
            ar: 'غروب الشمس على الشاطئ'
          }
        },
        g0ZA968_RpUe: {
          image: 'images/2372/HDRI/hdri_2.png',
          title: {
            en: 'City Landscape 2',
            ar: 'غروب الشمس على الشاطئ'
          }
        }
      },
      ambientLighting: {},
      colors: {
        interior: {
          '1ahry6ef040l': {
            label: {
              en: 'Black Leatherette',
              ar: 'جلد باللون الأسود'
            },
            image: 'images/2372/interiors/Black_Leatherette.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: '19I',
            brandItemCode: 'IN17',
          },
          a1e1txmplnmz: {
            label: {
              en: 'Beige Leatherette',
              ar: 'جلد باللون البيج'
            },
            image: 'images/2372/interiors/Beige_Leatherette.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: '4EO',
            brandItemCode: 'IN18',
          }
        },
        exterior: {
          k550ufl2z63j: {
            label: {
              en: 'Star Twinkle Black',
              ar: 'أسود'
            },
            image: '',
            hex: '#1A1A1A',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Star Twinkle Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 0,
                  UAE: 0,
                  OMR: 0,
                  BHD: 0,
                  QAR: 0,
                  JOD: 0,
                  KW: 0,
                  default: 0,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: '82U',
            brandItemCode: 'EX37',
          },
          gxqxsxnaljfj: {
            label: {
              en: 'Milky Tea',
              ar: 'بني فاتح'
            },
            image: '',
            hex: '#DCD5C4',
            usdz: 'red',
            price: {
              KSA: 745,
              UAE: 731,
              OMR: 77,
              BHD: 75,
              QAR: 723,
              JOD: 141,
              KW: 62,
              IQD: 199,
              LBP: 199,
              default: 731,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Milky Tea',
                  ar: 'بني فاتح'
                },
                price: {
                  KSA: 745,
                  UAE: 731,
                  OMR: 77,
                  BHD: 75,
                  QAR: 723,
                  JOD: 141,
                  KW: 62,
                  IQD: 199,
                  LBP: 199,
                  default: 731,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: '04U',
            brandItemCode: 'EX38',
          },
          '9i25yo15msgi': {
            label: {
              en: 'Gentle Gray with Star Twinkle Black Roof',
              ar: 'رمادي مع سقف أسود'
            },
            image: '',
            hex: '#8C8F8E',
            usdz: 'black',
            price: {
              KSA: 1119,
              UAE: 1098,
              OMR: 116,
              BHD: 113,
              QAR: 1086,
              JOD: 212,
              KW: 92,
              IQD: 298,
              LBP: 298,
              default: 1098,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Gentle Gray with Star Twinkle Black Roof',
                  ar: 'رمادي مع سقف أسود'
                },
                price: {
                  KSA: 1119,
                  UAE: 1098,
                  OMR: 116,
                  BHD: 113,
                  QAR: 1086,
                  JOD: 212,
                  KW: 92,
                  IQD: 298,
                  LBP: 298,
                  default: 1098,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: '88U',
            brandItemCode: 'EX39',
          },
          l415icrduyfe: {
            label: {
              en: 'Sea Blue with Polar White Roof',
              ar: 'أزرق مع سقف أبيض'
            },
            image: '',
            hex: '#79B4C9',
            usdz: 'blue',
            price: {
              KSA: 1119,
              UAE: 1098,
              OMR: 116,
              BHD: 113,
              QAR: 1086,
              JOD: 212,
              KW: 92,
              IQD: 298,
              LBP: 298,
              default: 1098,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sea Blue with Polar White Roof',
                  ar: 'أزرق مع سقف أبيض'
                },
                price: {
                  KSA: 1119,
                  UAE: 1098,
                  OMR: 116,
                  BHD: 113,
                  QAR: 1086,
                  JOD: 212,
                  KW: 92,
                  IQD: 298,
                  LBP: 298,
                  default: 1098,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G9H',
            brandItemCode: 'EX40',
          },
          t0ah6d0ze4xe: {
            label: {
              en: 'Tiger Blue with Star Twinkle Black Roof',
              ar: 'أزرق مع سقف أسود'
            },
            image: '',
            hex: '#0078A0',
            usdz: 'blue',
            price: {
              KSA: 1119,
              UAE: 1098,
              OMR: 116,
              BHD: 113,
              QAR: 1086,
              JOD: 212,
              KW: 92,
              IQD: 298,
              LBP: 298,
              default: 1098,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Tiger Blue with Star Twinkle Black Roof',
                  ar: 'أزرق مع سقف أسود'
                },
                price: {
                  KSA: 1119,
                  UAE: 1098,
                  OMR: 116,
                  BHD: 113,
                  QAR: 1086,
                  JOD: 212,
                  KW: 92,
                  IQD: 298,
                  LBP: 298,
                  default: 1098,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G6H',
            brandItemCode: 'EX41',
          },
          ew6nnknehod1: {
            label: {
              en: 'Track Yellow with Star Twinkle Black Roof',
              ar: 'أصفر مع سقف أسود'
            },
            image: '',
            hex: '#F4D00C',
            usdz: 'blue',
            price: {
              KSA: 1119,
              UAE: 1098,
              OMR: 116,
              BHD: 113,
              QAR: 1086,
              JOD: 212,
              KW: 92,
              IQD: 298,
              LBP: 298,
              default: 1098,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Track Yellow with Star Twinkle Black Roof',
                  ar: 'أصفر مع سقف أسود'
                },
                price: {
                  KSA: 1119,
                  UAE: 1098,
                  OMR: 116,
                  BHD: 113,
                  QAR: 1086,
                  JOD: 212,
                  KW: 92,
                  IQD: 298,
                  LBP: 298,
                  default: 1098,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GPX',
            brandItemCode: 'EX42',
          }
        }
      },
      features: {
        ylam62d7djz9: {
          label: {
            en: 'LED Headlights',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2372/features/LED headlamps.jpg',
          title: {
            en: 'LED Headlights',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'The front face combines sharp headlights, creating a strong visual impact.',
            ar: 'تتكامل المصابيح الأمامية حادة التصميم مع الواجهة الأمامية، لتمنح المركبة حضورًا بصريًا قويًا'
          },
          type: 'exterior'
        },
        kh1xk6tsachv: {
          label: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          image: 'images/2372/features/Wheels.jpg',
          title: {
            en: 'Wheels',
            ar: 'عجلات'
          },
          desc: {
            en: '16” Alloy Multi-spoke Wheel',
            ar: 'عجلات ألومنيوم متعددة الأضلاع قياس 16 إنش'
          },
          type: 'exterior'
        },
        '1slkxz7syxvp': {
          label: {
            en: 'LED Tail Lamps',
            ar: 'مصابيح خلفية LED'
          },
          image: 'images/2372/features/LED Tail Lamps.jpg',
          title: {
            en: 'LED Tail Lamps',
            ar: 'مصابيح خلفية LED'
          },
          desc: {
            en: 'The full-bodied rear taillights echo the front face, enhancing the vehicle’s recognizability.',
            ar: 'تكمّل المصابيح الخلفية ذات التصميم البارز ملامح الواجهة الأمامية، لتعزّز الهوية المميّزة للمركبة'
          },
          type: 'exterior'
        },
        k1gebz2rhrfs: {
          label: {
            en: 'Smart Charging',
            ar: 'الشحن الذكي'
          },
          image: 'images/2372/features/Smart Charging.jpg',
          title: {
            en: 'Smart Charging',
            ar: 'الشحن الذكي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        kbns5f4ez323: {
          label: {
            en: 'Roof Rails',
            ar: 'سكك سقفية'
          },
          image: 'images/2372/features/Roof Rails.jpg',
          title: {
            en: 'Roof Rails',
            ar: 'سكك سقفية'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        y6zcqp9r8fln: {
          label: {
            en: 'Infotainment Screen',
            ar: 'شاشة المعلومات والترفيه'
          },
          image: 'images/2372/features/Info Screen.jpg',
          title: {
            en: 'Infotainment Screen',
            ar: 'شاشة المعلومات والترفيه'
          },
          desc: {
            en: '10.1" Infotainment Screen with apple carplay & android auto',
            ar: 'شاشة لمس للنظام المعلوماتي الترفيهي قياس 10.1 إنش، مع ®Apple CarPlay و™Android Auto'
          },
          type: 'interior'
        },
        xsdac8nojc1s: {
          label: {
            en: 'Modern Interior',
            ar: 'مقصورة عصرية'
          },
          image: 'images/2372/features/Modern Interior.jpg',
          title: {
            en: 'Modern Interior',
            ar: 'مقصورة عصرية'
          },
          desc: {
            en: 'Experience the luxury of sleek and refined interior withing the Spark EUV',
            ar: 'استمتع بفخامة مقصورة سبارك EUV العصرية والأنيقة'
          },
          type: 'interior'
        },
        ddlwz0n6pzn89: {
          label: {
            en: 'Smart Storage',
            ar: 'تخزين ذكي'
          },
          image: 'images/2372/features/Versatile Space.jpg',
          title: {
            en: 'Smart Storage',
            ar: 'تخزين ذكي'
          },
          desc: {
            en: 'Smart storage space under the centre console',
            ar: 'مساحة تخزين ذكية أسفل الكونسول الوسطي'
          },
          type: 'interior'
        },
        k1gebz2rhrfs7: {
          label: {
            en: 'Buckle to Drive',
            ar: 'اربط وانطلق'
          },
          image: 'images/2372/features/Seatbelt.png',
          title: {
            en: 'Buckle to Drive',
            ar: 'اربط وانطلق'
          },
          desc: {
            en: 'Ensures that the driver must fasten their seatbelt before the vehicle can be driven. This system is designed to be user-friendly, providing reminders and alerts to the driver to buckle up before starting the journey',
            ar: 'يضمن هذا النظام أن يقوم السائق بربط حزام الأمان قبل أن يتمكن من القيادة. صُمم ليكون سهل الاستخدام، مع توفير تذكيرات وتنبيهات للسائق لربط الحزام قبل بدء الرحلة'
          },
          type: 'interior'
        },
        ddlwz0n6pzn8: {
          label: {
            en: 'Versatile Space',
            ar: 'مساحة متعددة الاستخدامات'
          },
          image: 'images/2372/features/Versatile Space.jpg',
          title: {
            en: 'Versatile Space',
            ar: 'مساحة متعددة الاستخدامات'
          },
          desc: {
            en: 'The Spark EUV boasts spacious seating for upto 4 passengers. With 428 liters of cargo space, which easily fits your daily essentials.',
            ar: 'توفّر سبارك EUV مقاعد رحبة تتسع لما يصل إلى 4 ركاب، ومساحة تخزين تبلغ 428 لترًا تستوعب مستلزماتك اليومية بكل سهولة'
          },
          type: 'interior'
        },
        '9cffzj69hili': {
          label: {
            en: 'Driving Mode',
            ar: 'أوضاع القيادة'
          },
          image: 'images/2372/features/Drivingmode.jpg',
          title: {
            en: 'Driving Mode',
            ar: 'أوضاع القيادة'
          },
          desc: {
            en: 'From Eco to Sports mode, driving the Spark EUV will up your EV game',
            ar: 'من وضعية Eco إلى وضعية Sport، ستأخذك قيادة سبارك EUV إلى مستوى جديد في عالم المركبات الكهربائية'
          },
          type: 'interior'
        },
        '9cffzj69hilli': {
          label: {
            en: '360 Degree Camera',
            ar: 'كاميرا 360°'
          },
          image: 'images/2372/features/360_Camera.jpg',
          title: {
            en: '360 Degree Camera',
            ar: 'كاميرا 360°'
          },
          desc: {
            en: 'Have complete visibility of the Spark EUV when you can see your car from any and every angle',
            ar: 'استمتع برؤية كاملة لمركبتك سبارك EUV من جميع الزوايا'
          },
          type: 'interior'
        }
      },
      accessories: {
        '0agqewqxtxza': {
          label: {
            en: 'Front Body Kit',
            ar: 'طقم الهيكل الأمامي'
          },
          image: 'images/2372/Acc/ACC_Front_Body_Kit.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'أكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC54',
          brandItemCode: 'AC54',
        },
        an4yb9ia2pck: {
          label: {
            en: 'Rear Body Kit',
            ar: 'طقم الهيكل الخلفي'
          },
          image: 'images/2372/Acc/ACC_Rear_Body_Kit.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'أكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC55',
          brandItemCode: 'AC55',
        },
        xfkdiah0cnde: {
          label: {
            en: 'Assist Steps',
            ar: 'درجات المساعدة على الصعود والنزول'
          },
          image: 'images/2372/Acc/ACC_Assist_Steps.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'أكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC56',
          brandItemCode: 'AC56',
        },
        tapbu8okt0uj: {
          label: {
            en: 'Side Molding',
            ar: 'قوالب جانبية للهيكل'
          },
          image: 'images/2372/Acc/ACC_Side_Molding.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'أكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC57',
          brandItemCode: 'AC57',
        },
        '4by5qhwuy594': {
          label: {
            en: 'Rear Storage Box',
            ar: 'صندوق تخزين خلفي'
          },
          image: 'images/2372/Acc/ACC_Rear_Storage_Box.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'أكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC58',
          brandItemCode: 'AC58',
        },
        mr7qy5fo5qhm: {
          label: {
            en: 'Front Storage Box',
            ar: 'صندوق تخزين أمامي'
          },
          image: 'images/2372/Acc/ACC_Front_Storage_Box.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'أكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC59',
          brandItemCode: 'AC59',
        },
        wf9709z90ina: {
          label: {
            en: 'Chevrolet Front Script Nameplate',
            ar: 'شعار شفروليه الكتابي الأمامي'
          },
          image: 'images/2372/Acc/ACC_Chevrolet_Front_Script_Nameplate.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'أكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC60',
          brandItemCode: 'AC60',
        },
        '992852zsh6q9': {
          label: {
            en: 'Puddle Lamp',
            ar: 'إنارة أرضية أسفل الأبواب'
          },
          image: 'images/2372/Acc/ACC_Puddle_Lamp.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'أكسسوارات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '4ort1u09q912': {
          label: {
            en: 'Gaming Theme',
            ar: 'تصميم الألعاب'
          },
          image: 'images/2372/themes/Gaming_Theme.png',
          category: {
            en: 'Themes',
            ar: 'التصاميم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC61',
          brandItemCode: 'AC61',
        },
        myd4cnt41hwx: {
          label: {
            en: 'Camo Theme',
            ar: 'تصميم تمويهي'
          },
          image: 'images/2372/themes/Camo_Theme.png',
          category: {
            en: 'Themes',
            ar: 'التصاميم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC62',
          brandItemCode: 'AC62',
        },
        d5bu0trex0kw: {
          label: {
            en: 'Adventure Theme',
            ar: 'تصميم المغامرة'
          },
          image: 'images/2372/themes/Adventure_Theme.png',
          category: {
            en: 'Themes',
            ar: 'التصاميم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC63',
          brandItemCode: 'AC63',
        },
        ei4h2im8ratx: {
          label: {
            en: 'Active Theme',
            ar: 'تصميم رياضي'
          },
          image: 'images/2372/themes/Active_Theme.png',
          category: {
            en: 'Themes',
            ar: 'التصاميم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC64',
          brandItemCode: 'AC64',
        },
        '4fjgp644j9510': {
          label: {
            en: 'Retro Theme',
            ar: 'تصميم كلاسيكي'
          },
          image: 'images/2372/themes/Retro_Theme.png',
          category: {
            en: 'Themes',
            ar: 'التصاميم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC65',
          brandItemCode: 'AC65',
        },
        '4fjgp644j954': {
          label: {
            en: 'logo',
            ar: 'الشعار'
          },
          image: 'images/2372/themes/Retro_Theme.png',
          category: {
            en: 'Themes',
            ar: 'التصاميم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        }
      },
      packages: {},
      interactions: {
        xbdzchklx04d: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        sp7oz1hgrlpw: {
          label: {
            en: 'Bonnet',
            ar: 'غطاء المحرك'
          },
          type: 'interaction'
        },
        '9a4ewd0iqpwk': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        bkr8amy2tvbs: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '6146uzbd6kui': {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        iq7p37hq4g0j: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        }
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج'
          }
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول'
          }
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني'
          }
        }
      },
      name: {
        en: 'All-new Chevrolet Spark EUV',
        ar: 'شفروليه سبارك EUV الكهربائية الجديدة كلياً'
      },
      specifications: {
        heading: {
          en: 'All-new Chevrolet Spark EUV',
          ar: 'شفروليه سبارك EUV الكهربائية الجديدة كلياً'
        },
        trim: {
          en: '',
          ar: ''
        },
        price: {
          en: '',
          ar: ''
        },
        model: 'All-new Chevrolet Spark EUV',
        seatingCapacity: '4',
        dimensions: {
          length: '',
          width: '',
          height: '',
          wheelbase: '2560 mm',
          groundClearance: ''
        },
        performance: [
          {
            value: '360',
            unit: 'km',
            label: {
              en: 'Range*',
              ar: 'المدى*'
            }
          },
          {
            value: '180',
            unit: 'NM',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران'
            }
          },
          {
            value: '75Kw',
            unit: '',
            label: {
              en: '',
              ar: ''
            }
          }
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز'
            },
            subtitle: {
              en: 'All-new Chevrolet Spark EUV',
              ar: 'شفروليه سبارك EUV الكهربائية الجديدة كلياً'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية'
            },
            subtitle: {
              en: 'Star Twinkle Black, Milky Tea, Gentle Gray with Star Twinkle Black Roof, Sea Blue with Polar White Roof, Tiger Blue with Star Twinkle Black Roof, Track Yellow with Star Twinkle Black Roof',
              ar: 'أسود، بني فاتح، رمادي مع سقف أسود، أزرق مع سقف أبيض، أزرق مع سقف أسود، أصفر مع سقف أسود'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Interior',
              ar: 'المقصورة'
            },
            subtitle: {
              en: 'Black Leatherette or Beige Leatherette',
              ar: 'جلد باللون الأسود أو البيج'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه'
            },
            subtitle: {
              en: '10.1” Infotainment Screen with Apple Carplay & Android Auto',
              ar: 'شاشة لمس للنظام المعلوماتي الترفيهي قياس 10.1 إنش، مع ®Apple CarPlay و™Android Auto'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات'
            },
            subtitle: {
              en: '2560 mm',
              ar: '2560 مم'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'L x W x H',
              ar: 'الطول × العرض × الارتفاع'
            },
            subtitle: {
              en: '3996 mm X 1760 mm X 1726mm',
              ar: '3996 × 1760 × 1726 مم'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'مساحة التخزين'
            },
            subtitle: {
              en: '1375 Liters cargo volume behind 1st row with 2nd row folded with 428 Liters behind 2nd row seat',
              ar: 'مساحة تخزين تبلغ 1,375 لترًا خلف مقاعد الصف الأول عند طي مقاعد الصف الثاني، و428 لترًا خلف مقاعد الصف الثاني'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Powertrain',
              ar: 'مجموعة نقل الحركة'
            },
            subtitle: {
              en: 'Front Wheel Drive',
              ar: 'دفع أمامي'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Smart Charging',
              ar: 'الشحن الذكي'
            },
            subtitle: {
              en: 'Smart charging with convenient level 1 charging and standard 7KW wallbox for overnight charging at home',
              ar: 'شحن منزلي مريح باستخدام الشاحن المحمول من المستوى 1 أو الشاحن الجداري المنزلي القياسي بقدرة 7 كيلوواط، للحصول على شحن كامل طوال الليل'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Battery Warranty',
              ar: 'ضمان البطارية'
            },
            subtitle: {
              en: '8 years or 160K KM',
              ar: '8 سنوات أو 160,000 كم، أيهما يسبق'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'سعة المقاعد'
            },
            subtitle: {
              en: '4',
              ar: '4'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          }
        ]
      },
      accessoryCategories: {
        exterior: [
          'Themes'
        ],
        interior: [],
        options: [
          'Options & Accessories'
        ]
      }
    }
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: { en: 'Select Body Style', ar: 'اختر نمط الهيكل' },
      availableTrims: { en: 'Select Trim', ar: 'اختر الفئة' },
      driveType: { en: 'Drive Type', ar: 'نوع القيادة' },
      model: { en: 'Model', ar: 'الموديل' },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: { en: 'Loading', ar: 'جارٍ التحميل' },
      variantLoading: { en: 'Trim is loading', ar: 'نموذج جارٍ التحميل' },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: { en: 'Go Back', ar: 'العودة' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'عرض مميز' },
      configurator: { en: 'Configurator', ar: 'المُكوِّن' },
      buildYourRide: { en: 'Build Your Ride', ar: 'ابنِ سيارتك' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
      saveYourBuild: { en: 'Save Your Build', ar: 'احفظ تكوينك' },
      startingPrice: { en: 'Starting Price', ar: 'السعر الابتدائي' },
      msrp: { en: 'MSRP', ar: 'سعر التجزئة المقترح' },
      proceed: { en: 'Proceed', ar: 'متابعة' },
    },
    tabs: {
      config: { en: 'Config', ar: 'الإعداد' },
      model: { en: 'Model', ar: 'الموديل' },
      exterior: { en: 'Exterior', ar: 'الخارجي' },
      interior: { en: 'Interior', ar: 'الداخلي' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: { en: 'Remove', ar: 'إزالة' },
      packageCategory: { en: 'Package', ar: 'حزمة' },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: { en: 'Save to WhatsApp', ar: 'حفظ على واتساب' },
      optionEmail: { en: 'Save to Email', ar: 'حفظ على البريد الإلكتروني' },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: { en: 'Main Menu', ar: 'القائمة الرئيسية' },
      search: { en: 'Search', ar: 'بحث' },
      settings: { en: 'Settings', ar: 'الإعدادات' },
      profile: { en: 'Profile', ar: 'الملف الشخصي' },
      notifications: { en: 'Notifications', ar: 'الإشعارات' },
      help: { en: 'Help & Support', ar: 'المساعدة والدعم' },
      filter: { en: 'Filter Options', ar: 'خيارات التصفية' },
      favorites: { en: 'Favorites', ar: 'المفضلة' },
      history: { en: 'History', ar: 'التاريخ' },
      tools: { en: 'Tools', ar: 'الأدوات' },
      variants: { en: 'Variants', ar: 'الأنواع' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'ألوان المقصورة' },
      features: { en: 'Features', ar: 'الميزات' },
      step: { en: 'Step', ar: 'خطوة' },
      openPanel: { en: 'Open Panel', ar: 'فتح اللوحة' },
      closePanel: { en: 'Close Panel', ar: 'إغلاق اللوحة' },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: { en: 'Ext', ar: 'خارجي' },
      int: { en: 'Int', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
    },
    bottomDrawer: {
      variants: { en: 'Variants', ar: 'الأنواع' },
      interactions: { en: 'Interactions', ar: 'التفاعلات' },
      accessories: { en: 'Options & Accessories', ar: 'الإكسسوارات' },
      features: { en: 'Features', ar: 'الميزات' },
      highlights: { en: 'Highlights', ar: 'الميزات البارزة' },
      moreOptions: { en: 'More Options', ar: 'المزيد من الخيارات' },
      colorSelector: { en: 'Color Selector', ar: 'محدد اللون' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      wheels: { en: 'Wheels', ar: 'العجلات' },
      roofs: { en: 'Roofs', ar: 'الأسقف' },
      performance: { en: 'Performance', ar: 'الأداء' },
      packages: { en: 'Packages', ar: 'الحزم' },
      chargingOptions: { en: 'Charging Options', ar: 'خيارات الشحن' },
      safetyServices: { en: 'Safety & Services', ar: 'السلامة والخدمات' },
      expand: { en: 'Expand', ar: 'توسيع' },
      minimize: { en: 'Minimize', ar: 'تصغير' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'العرض المميز' },
    },
    qr: {
      scan: { en: 'Scan QR', ar: 'امسح رمز الاستجابة السريعة' },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: { en: 'Share Configuration', ar: 'مشاركة التكوين' },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my All-new Chevrolet Spark EUV configuration!',
        ar: 'شاهد تكوين سيارتي All-new Chevrolet Spark EUV!',
      },
      whatsappTitle: {
        en: 'Check out my All-new Chevrolet Spark EUV configuration!',
        ar: 'شاهد تكوين سيارتي All-new Chevrolet Spark EUV!',
      },
      linkedinTitle: {
        en: 'My All-new Chevrolet Spark EUV Configuration',
        ar: 'تكويني لسيارة All-new Chevrolet Spark EUV',
      },
      linkedinSummary: {
        en: 'Check out my custom All-new Chevrolet Spark EUV configuration!',
        ar: 'شاهد تكويني المخصص لسيارة All-new Chevrolet Spark EUV!',
      },
      emailSubject: {
        en: 'Check out my All-new Chevrolet Spark EUV Configuration',
        ar: 'شاهد تكوين سيارتي All-new Chevrolet Spark EUV',
      },
      emailBody: {
        en: 'I configured my dream All-new Chevrolet Spark EUV. Take a look:',
        ar: 'قمت بتكوين سيارة All-new Chevrolet Spark EUV المثالية. ألق نظرة:',
      },
      linkLabel: { en: 'Or copy link:', ar: 'أو انسخ الرابط:' },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: { en: 'Copy', ar: 'نسخ' },
      saveAndShare: { en: 'Save & Share', ar: 'حفظ ومشاركة' },
      copyLink: { en: 'Copy link', ar: 'نسخ الرابط' },
      facebook: { en: 'Facebook', ar: 'Facebook' },
      twitter: { en: 'Twitter', ar: 'Twitter' },
      gmail: { en: 'Gmail', ar: 'Gmail' },
      whatsapp: { en: 'Whatsapp', ar: 'Whatsapp' },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: { en: 'ex showroom price', ar: 'سعر صالة العرض' },
      leads: { en: 'Save', ar: 'حفظ' },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: { en: 'Save', ar: 'حفظ' },
      item: { en: 'Item', ar: 'العنصر' },
      items: { en: 'Items', ar: 'عناصر' },
      totalToPay: { en: 'Total to pay', ar: 'الإجمالي للدفع' },
      bookTestDrive: { en: 'Save', ar: 'حفظ' },
      requestQuote: { en: 'Request a Quote', ar: 'طلب عرض سعر' },
      downloadBrochure: { en: 'Download Brochure', ar: 'تحميل الكتيب' },
    },
    priceSummary: {
      title: { en: 'Price Summary', ar: 'ملخص السعر' },
      enterLocation: { en: 'Enter Location', ar: 'أدخل الموقع' },
      selectCountry: { en: 'Location', ar: 'الموقع' },
      selectCity: { en: 'Location', ar: 'الموقع' },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: { en: 'Price Breakdown', ar: 'تفصيل السعر' },
      color: { en: 'Color', ar: 'اللون' },
      variant: { en: 'Variant', ar: 'الفئة' },
      included: { en: 'Included', ar: 'مشمول' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    featureDialog: {
      playPause: { en: 'Play-Pause', ar: 'تشغيل-إيقاف' },
    },
    selectDealership: {
      title: { en: 'Select a Dealer', ar: 'اختر وكيلاً' },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: { en: 'UAE', ar: 'الإمارات' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      dealersInCity: { en: 'Dealers in {{city}}', ar: 'وكلاء في {{city}}' },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: { en: 'UAE', ar: 'الإمارات' },
      KSA: { en: 'KSA', ar: 'السعودية' },
      KW: { en: 'Kuwait', ar: 'الكويت' },
      default: { en: 'UAE', ar: 'الإمارات' },
    },
    localMatches: {
      exterior: { en: 'Exterior', ar: 'خارجي' },
      interior: { en: 'Interior', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
      yourConfiguration: { en: 'Current Configuration', ar: 'التكوين الحالي' },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: { en: 'Your Build Details', ar: 'تفاصيل بنائك' },
      options: { en: 'Options', ar: 'الخيارات' },
      save: { en: 'Save', ar: 'حفظ' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      changeDealer: { en: 'Change Dealer', ar: 'تغيير الوكيل' },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: { en: 'Matches found', ar: 'تطابق تم العثور عليه' },
      startingFrom: { en: 'Starting from', ar: 'يبدأ من' },
      getQuote: { en: 'Proceed', ar: 'احصل على عرض سعر' },
      viewDifferences: { en: 'View Differences', ar: 'عرض الاختلافات' },
      match: { en: 'Match', ar: 'تطابق' },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: { en: 'Build Details', ar: 'تفاصيل التكوين' },
      trim: { en: 'Trim', ar: 'الفئة' },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: { en: 'Exact Match', ar: 'تطابق تام' },
      similarMatch: { en: 'Close Match', ar: 'تطابق قريب' },
      availableNow: { en: 'Available Now', ar: 'متوفر الآن' },
      whatYouConfigured: { en: 'What you configured', ar: 'ما قمت بتكوينه' },
      matchingInStock: { en: 'Matching vehicle available in stock', ar: 'مركبة مطابقة متوفرة في المخزون' },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: { en: 'Close Match Details', ar: 'تفاصيل التطابق القريب' },
      matchesYourSelection: { en: 'Matches your selection', ar: 'يطابق اختيارك' },
      sameAsWhatYouSelected: { en: 'Same as what you selected', ar: 'نفس ما اخترته' },
      replacesYourSelection: { en: 'Replaces your selection', ar: 'يستبدل اختيارك' },
      selectionNotIncluded: { en: 'Selection not included', ar: 'الاختيار غير مشمول' },
      includedInThisVehicle: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      additionalFeatureAlreadyIncluded: { en: 'Additional feature already included', ar: 'ميزة إضافية مشمولة بالفعل' },
      stockDisclaimer: { en: 'Available stock may include extra features not selected in your original configuration.', ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.' },
      additionalInstalledOptionsLegend: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      vehicleDetails: { en: 'Vehicle Details', ar: 'تفاصيل المركبة' },
      installedOptionsTitle: { en: 'Comes with vehicle', ar: 'يأتي مع المركبة' },
      additionalInstalledOptionsSubtitle: { en: 'Additional Installed Options:', ar: 'الخيارات المركبة الإضافية:' },
      specTrim: { en: 'Trim', ar: 'الفئة' },
      specExteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      specInteriorColor: { en: 'Interior Color', ar: 'اللون الداخلي' },
      specWheels: { en: 'Wheels', ar: 'العجلات' },
      specPackage: { en: 'Package', ar: 'الحزمة' },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: { en: 'Trim Selection', ar: 'اختيار الفئة' },
    },
    paymentPage: {
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      accessory: { en: 'Accessory', ar: 'ملحق' },
      closePayment: { en: 'Close', ar: 'إغلاق' },
    },
    packageModal: {
      includes: { en: 'Includes:', ar: 'يشمل:' },
      of: { en: 'of', ar: 'من' },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: { en: 'Item', ar: 'غرض' },
      items: { en: 'Items', ar: 'أغراض' },
      addPackage: { en: 'Add Package', ar: 'إضافة الحزمة' },
      noThanks: { en: 'No Thanks', ar: 'لا شكراً' },
      alreadyApplied: { en: 'Already Applied', ar: 'مُطبّق بالفعل' },
    },
    changesRequired: { en: 'Changes Required', ar: 'التغييرات المطلوبة' },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: { en: 'Apply Changes', ar: 'تطبيق التغييرات' },
    previewFeature: { en: 'Preview Feature', ar: 'معاينة الميزة' },
    exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    buildAndPrice: { en: 'Build & Price', ar: 'البناء والسعر' },
    features: {
      preview: { en: 'Preview Feature', ar: 'معاينة المميزات' },
      playAll: { en: 'PLAY ALL', ar: 'تشغيل الكل' },
      title: { en: 'Features', ar: 'المميزات' },
    },
    header: {
      localMatches: { en: 'Local\nMatches', ar: 'التطابقات\nالمحلية' },
      matchingVehicles: { en: 'Matching vehicles', ar: 'مركبات مطابقة' },
      configurationsFound: { en: 'Configurations Found', ar: 'تكوينات تم العثور عليها' },
    },
    configPanel: {
      playAll: { en: 'Play All', ar: 'تشغيل الكل' },
      pause: { en: 'Pause', ar: 'إيقاف مؤقت' },
      exteriorColors: { en: 'Exterior Colors', ar: 'ألوان الهيكل الخارجي' },
      interiorColors: { en: 'Interior Colors', ar: 'ألوان الداخلية' },
      roofOptions: { en: 'Roof Options', ar: 'خيارات السقف' },
      interiorOptions: { en: 'Interior Options', ar: 'خيارات الداخلية' },
      exteriorAccessories: { en: 'Exterior Accessories', ar: 'إكسسوارات خارجية' },
      noAdditionalOptions: { en: 'No additional options available for the selected trim.', ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.' },
      noFilters: { en: 'No filters available.', ar: 'لا توجد فلاتر متاحة.' },
      noVariantsMatch: { en: 'No trims match the selected filters.', ar: 'لا توجد فئات تطابق الفلاتر المختارة.' },
      configurationBreakdown: { en: 'Configuration Breakdown', ar: 'تفاصيل التكوين' },
      yourConfiguration: { en: 'Your Configuration', ar: 'تكوينك' },
      trim: { en: 'Trim', ar: 'الفئة' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    conflictDialog: {
      description: { en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.', ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.' },
    },
    viewport: {
      changeBackground: { en: 'Change Background', ar: 'تغيير الخلفية' },
      selectLanguage: { en: 'Switch Language (عربي)', ar: 'تبديل اللغة (EN)' },
      menu: { en: 'Menu', ar: 'القائمة' },
      exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '274766',
        dealerName: 'Jaidah',
        label: {
          en: 'Jaidah Square Showroom - Al Matar Street Umm Ghuwallina, Doha',
          ar: 'صالة عرض "جايدة سكوير" - شارع المطار، أم غويلينة، الدوحة',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '122219',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '234217',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Al Farq Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260896',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Hail Al Shumaliah Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع حيل الشمالية',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: true,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'spark euv',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'SparkEUV',
    modelName: 'SparkEUV',
    pageTitle: 'Spark EUV Configurator',
    client: 'GM',
    brand: 'chevrolet',
    modelId: 'CHSPAEUV2026',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      vehicleType: 'suv',
      brand: 'chevrolet',
      provider: 'adobe',
      modelName: 'spark euv'
    },
    presentation: {
      video: 'videos/2372/presentation_video_1.webm',
      loadingImage: 'images/2372/sliderImage.webp',
      transitionImage: 'images/2372/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: 'Range*',
            ar: 'المدى*'
          },
          value: {
            en: '360 km',
            ar: '360 كم'
          },
          unit: ''
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران'
          },
          value: {
            en: '180 NM',
            ar: '180 نيوتن متر'
          },
          unit: ''
        },
        {
          index: 2,
          show: true,
          title: {
            en: '',
            ar: ''
          },
          value: {
            en: '75Kw',
            ar: '75 كيلوواط'
          },
          unit: ''
        }
      ]
    }
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: ['email', 'test-drive', 'callback'],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: ['whatsapp', 'test-drive', 'callback'],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: ['test-drive', 'callback'],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: ['test-drive', 'callback'],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    }
  },
  filterOptions: [
    {
      id: 'price-range',
      key: 'price-range',
      label: {
        en: 'Price Range',
        ar: 'Price Range'
      },
      disabled: true,
      type: 'checkbox',
      options: [
        {
          id: 'price-range-1',
          label: {
            en: '60 Lakhs - 70 Lakhs',
            ar: '60 Lakhs - 70 Lakhs'
          },
          value: '60 Lakhs - 70 Lakhs',
          checked: false
        },
        {
          id: 'price-range-2',
          label: {
            en: '70 Lakhs - 80 Lakhs',
            ar: '70 Lakhs - 80 Lakhs'
          },
          value: '70 Lakhs - 80 Lakhs',
          checked: false
        },
        {
          id: 'price-range-3',
          label: {
            en: '80 Lakhs - 90 Lakhs',
            ar: '80 Lakhs - 90 Lakhs'
          },
          value: '80 Lakhs - 90 Lakhs',
          checked: false
        }
      ]
    },
    {
      id: 'engine',
      key: 'engine',
      label: {
        en: 'Engine',
        ar: 'محرك'
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: 'petrol',
          label: {
            en: 'Petrol',
            ar: 'Petrol'
          },
          value: 'Petrol',
          checked: false
        },
        {
          id: 'turbo-petrol',
          label: {
            en: 'Turbo Petrol',
            ar: 'Turbo Petrol'
          },
          value: 'Turbo Petrol',
          checked: false
        }
      ]
    },
    {
      id: 'transmission',
      key: 'transmission',
      label: {
        en: 'Transmission',
        ar: 'انتقال'
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: 'cvt',
          label: {
            en: 'CVT',
            ar: 'CVT'
          },
          value: 'CVT',
          checked: false
        },
        {
          id: 'mt',
          label: {
            en: 'MT',
            ar: 'MT'
          },
          value: 'MT',
          checked: false
        },
        {
          id: 'amt',
          label: {
            en: 'AMT',
            ar: 'AMT'
          },
          value: 'AMT',
          checked: false
        }
      ]
    }
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': 'Chevy Sans',
      '--bold-font-family': 'Chevy Sans Medium',
    },
  },
} as unknown as UIMetadataComplete;
